import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const PickupDetailsStep = (ctx) => {
  const {
    selectedLocation, setSelectedLocation,
    locationType, setLocationType,
    workplaceAddress, setWorkplaceAddress,
    businessName, setBusinessName,
    returnDate, setReturnDate,
    selectedTime, setSelectedTime,
    errors,
    locations,
    blackoutDates,
    serviceAreaZips,
    zipCodeValid,
    validateZipCode,
    checkIfSameDay,
    handleNextStep,
    savedAddress,
  } = ctx;

  return (
    <>
            {/* Single Unified Location Dropdown */}
            <div className="form-group">
              <Label htmlFor="pickupLocation">Choose a Pickup Location *</Label>
              <Select value={selectedLocation} onValueChange={(value) => {
                setSelectedLocation(value);
                if (value === 'workplace') {
                  setLocationType('workplace');
                  setWorkplaceAddress({ street: '', apartment: '', city: 'Scottsdale', state: 'AZ', zipCode: '', pickupNotes: '' });
                } else {
                  setLocationType('office');
                }
              }}>
                <SelectTrigger className={errors.location ? 'input-error' : ''}>
                  <SelectValue placeholder="Select where we should pick up your packages" />
                </SelectTrigger>
                <SelectContent>
                  {/* Preloaded Office Locations */}
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ fontWeight: '500' }}>{location.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{location.address}</div>
                      </div>
                    </SelectItem>
                  ))}
                  
                  {/* Business/Workplace Pickup Option */}
                  <SelectItem value="workplace">
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontWeight: '500' }}>🏢 Business/Workplace Pickup</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Enter your office/workplace address</div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {errors.location && <span className="error-message">{errors.location}</span>}
            </div>

            {/* IWG Disclaimer (shown when preloaded location is selected) */}
            {selectedLocation && selectedLocation !== 'workplace' && (
              <div style={{ 
                padding: '0.875rem', 
                backgroundColor: '#fef3c7', 
                border: '1px solid #f59e0b',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
                  <AlertCircle size={18} style={{ color: '#92400e', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ fontSize: '0.8125rem', color: '#92400e', lineHeight: 1.5 }}>
                    <strong>Important Notice:</strong> ReturnPal operates independently of and is not affiliated with IWG Brands. IWG Brands assumes no responsibility or liability for the handling, custody, loss, damage, or disposition of any packages.
                  </div>
                </div>
              </div>
            )}

            {/* Workplace Address Input */}
            {locationType === 'workplace' && selectedLocation === 'workplace' && (
              <div className="form-group">
                {/* Saved address suggestion */}
                {savedAddress && savedAddress.street && (
                  <div
                    style={{
                      padding: '0.85rem 1rem',
                      backgroundColor: '#f0fdf4',
                      border: '2px solid #10b981',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                    }}
                    data-testid="saved-address-banner"
                  >
                    <div style={{ flex: '1 1 240px', fontSize: '0.85rem', color: '#065f46' }}>
                      <div style={{ fontWeight: 700, marginBottom: '0.15rem' }}>📌 Use your last saved address?</div>
                      <div style={{ color: '#047857' }}>
                        {savedAddress.street}{savedAddress.apartment ? `, ${savedAddress.apartment}` : ''}, {savedAddress.city}, {savedAddress.state} {savedAddress.zipCode}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setWorkplaceAddress({ ...savedAddress })}
                      data-testid="use-saved-address-btn"
                      style={{ backgroundColor: '#10b981', color: 'white', fontWeight: 600, fontSize: '0.8rem' }}
                    >
                      Use saved address
                    </Button>
                  </div>
                )}

                {/* Important Warning */}
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#fef2f2', 
                  border: '2px solid #ef4444',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
                    <AlertCircle size={20} style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '0.25rem' }}>
                        ⚠️ Important: You MUST Be Present
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#991b1b', lineHeight: 1.5 }}>
                        For workplace pickups, you must be present to hand over your packages. 
                        <strong> Nothing can be left at reception/doorstep due to theft prevention.</strong> 
                        Please ensure someone is available at the specified pickup time.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Zip Code First */}
                <div style={{ marginBottom: '1rem' }}>
                  <Label htmlFor="zipCode">Enter Office Building Zip Code *</Label>
                  <Input
                    id="zipCode"
                    value={workplaceAddress.zipCode}
                    onChange={(e) => {
                      const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setWorkplaceAddress({...workplaceAddress, zipCode: zip});
                      if (zip.length === 5) {
                        validateZipCode(zip);
                      } else {
                        setZipCodeValid(true);
                        setErrors(prev => {
                          const newErrors = {...prev};
                          delete newErrors.zipCode;
                          return newErrors;
                        });
                      }
                    }}
                    maxLength="5"
                    placeholder="85254"
                    className={errors.zipCode || !zipCodeValid ? 'input-error' : ''}
                    style={{ fontSize: '1.1rem', padding: '0.75rem' }}
                  />
                  {workplaceAddress.zipCode.length === 5 && !zipCodeValid && (
                    <div style={{ 
                      marginTop: '0.75rem', 
                      padding: '1rem', 
                      backgroundColor: '#fef3c7', 
                      border: '2px solid #f59e0b',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '0.5rem' }}>
                        😔 Sorry, ReturnPal is working on expanding our service area
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.75rem' }}>
                        Check back soon for added service areas, or choose to <strong>drop off at one of our office locations</strong> instead!
                      </div>
                      <Button
                        type="button"
                        onClick={() => {
                          setLocationType('office');
                          setWorkplaceAddress({ street: '', apartment: '', city: 'Scottsdale', state: 'AZ', zipCode: '', pickupNotes: '' });
                          setZipCodeValid(true);
                        }}
                        style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem'
                        }}
                      >
                        Switch to Office Location
                      </Button>
                    </div>
                  )}
                  {workplaceAddress.zipCode.length === 5 && zipCodeValid && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      padding: '0.75rem', 
                      backgroundColor: '#d1fae5', 
                      border: '1px solid #10b981',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#065f46'
                    }}>
                      ✅ Great! We service your area - {workplaceAddress.zipCode}
                    </div>
                  )}
                </div>

                {/* Address Fields (only show if zip is valid) */}
                {workplaceAddress.zipCode.length === 5 && zipCodeValid && (
                  <>
                    <Label htmlFor="street">Office Building Address *</Label>
                    <Input
                      id="street"
                      value={workplaceAddress.street}
                      onChange={(e) => setWorkplaceAddress({...workplaceAddress, street: e.target.value})}
                      placeholder="123 Main Street"
                      className={errors.street ? 'input-error' : ''}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    {errors.street && <span className="error-message">{errors.street}</span>}

                    <Label htmlFor="apartment" style={{ marginTop: '0.75rem' }}>Suite / Floor (Optional)</Label>
                    <Input
                      id="apartment"
                      value={workplaceAddress.apartment}
                      onChange={(e) => setWorkplaceAddress({...workplaceAddress, apartment: e.target.value})}
                      placeholder="Suite 500, Floor 5, etc."
                      style={{ marginBottom: '0.75rem' }}
                    />

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={workplaceAddress.city}
                          onChange={(e) => setWorkplaceAddress({...workplaceAddress, city: e.target.value})}
                          className={errors.city ? 'input-error' : ''}
                        />
                        {errors.city && <span className="error-message">{errors.city}</span>}
                      </div>
                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          value={workplaceAddress.state}
                          onChange={(e) => setWorkplaceAddress({...workplaceAddress, state: e.target.value})}
                          maxLength="2"
                          className={errors.state ? 'input-error' : ''}
                        />
                        {errors.state && <span className="error-message">{errors.state}</span>}
                      </div>
                    </div>

                    {/* Pickup Notes */}
                    <div style={{ marginTop: '1rem' }}>
                      <Label htmlFor="pickupNotes">Pickup Instructions / Notes *</Label>
                      <textarea
                        id="pickupNotes"
                        value={workplaceAddress.pickupNotes}
                        onChange={(e) => setWorkplaceAddress({...workplaceAddress, pickupNotes: e.target.value})}
                        placeholder="Example: Meet at main lobby reception desk. Ask for John Smith. Building requires visitor check-in."
                        className={errors.pickupNotes ? 'input-error' : ''}
                        rows="4"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                      {errors.pickupNotes && <span className="error-message">{errors.pickupNotes}</span>}
                      <p className="field-note" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                        Include: Where to meet you, building access instructions, contact person, parking info, etc.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Business Name Field (conditionally shown for Scottsdale Quarter Businesses) */}
            {selectedLocation === 'scottsdale-quarter-business' && (
              <div className="form-group">
                <Label htmlFor="businessName">
                  Business Name <span style={{ color: '#ef4444' }}>*</span>
                </Label>
                <Input
                  id="businessName"
                  type="text"
                  value={businessName}
                  onChange={(e) => {
                    setBusinessName(e.target.value);
                    if (errors.businessName) setErrors(prev => ({ ...prev, businessName: '' }));
                  }}
                  placeholder="e.g., Sephora, Lululemon, etc."
                  className={errors.businessName ? 'input-error' : ''}
                />
                <p className="field-note">Enter the business name where packages should be picked up</p>
                {errors.businessName && <span className="error-message">{errors.businessName}</span>}
              </div>
            )}

            {/* Pickup Date */}
            <div className="form-group">
              <Label htmlFor="pickupDate">Select Pickup Date</Label>
              <Input
                id="pickupDate"
                type="date"
                value={returnDate}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const selectedDay = new Date(selectedDate + 'T00:00:00').getDay();
                  
                  // Check if weekend (0 = Sunday, 6 = Saturday)
                  if (selectedDay === 0 || selectedDay === 6) {
                    setErrors(prev => ({
                      ...prev,
                      returnDate: '🚫 Weekend pickups are not available. Please choose a weekday (Monday-Friday).'
                    }));
                    setReturnDate('');
                    return;
                  }
                  
                  // Check if blackout date
                  if (blackoutDates.includes(selectedDate)) {
                    setErrors(prev => ({
                      ...prev,
                      returnDate: '🚫 This date is unavailable. Please choose another date.'
                    }));
                    setReturnDate('');
                    return;
                  }
                  
                  setReturnDate(selectedDate);
                  if (errors.returnDate) setErrors(prev => ({ ...prev, returnDate: '' }));
                }}
                className={errors.returnDate ? 'input-error' : ''}
                min={new Date().toISOString().split('T')[0]}
              />
              
              {/* Date Availability Legend */}
              <div style={{ 
                marginTop: '0.75rem', 
                padding: '0.75rem', 
                backgroundColor: '#f9fafb', 
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                  📅 Pickup Availability:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: '#10b981', 
                      borderRadius: '2px',
                      display: 'inline-block'
                    }}></span>
                    <span style={{ color: '#065f46' }}>✓ Weekdays (Monday-Friday) - Available</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: '#ef4444', 
                      borderRadius: '2px',
                      display: 'inline-block'
                    }}></span>
                    <span style={{ color: '#991b1b' }}>✗ Weekends (Sat/Sun) - Not Available</span>
                  </div>
                  {blackoutDates.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ 
                        width: '12px', 
                        height: '12px', 
                        backgroundColor: '#f59e0b', 
                        borderRadius: '2px',
                        display: 'inline-block'
                      }}></span>
                      <span style={{ color: '#92400e' }}>⊗ Blocked Dates - {blackoutDates.length} date(s) unavailable</span>
                    </div>
                  )}
                </div>
              </div>

              {errors.returnDate && <span className="error-message">{errors.returnDate}</span>}
            </div>

            {/* Time Selection */}
            <div className="form-group">
              <Label htmlFor="time">Select Pickup Window</Label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger className={errors.time ? 'input-error' : ''}>
                  <SelectValue placeholder="Choose a collection window" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="11 AM - 1 PM">11 AM - 1 PM (Midday Pickup)</SelectItem>
                  <SelectItem value="4-6 PM">4-6 PM (End-of-Day Pickup)</SelectItem>
                </SelectContent>
              </Select>
              <p className="field-note">We pick up packages Monday–Friday during one of two windows: 11 AM–1 PM or 4–6 PM</p>
              {errors.time && <span className="error-message">{errors.time}</span>}
            </div>


      {/* Step 1 navigation */}
      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="button"
          onClick={handleNextStep}
          data-testid="wizard-next-btn"
          className="btn-primary btn-large"
          style={{ minWidth: '200px' }}
        >
          Next: Packages →
        </Button>
      </div>
    </>
  );
};

export default PickupDetailsStep;
